
import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CalendarSource, CalendarEvent } from '@/types/calendar';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CalendarDays, Plus, Clock, CalendarRange, User, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';

interface CalendarSidebarProps {
  sources: CalendarSource[];
  onSourceToggle: (sourceId: string) => void;
  upcomingEvents: CalendarEvent[];
}

const CalendarSidebar: React.FC<CalendarSidebarProps> = ({ 
  sources, 
  onSourceToggle,
  upcomingEvents
}) => {
  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const [connectingSource, setConnectingSource] = useState<'google' | 'apple'>('google');
  
  const getNextUpcomingEvent = () => {
    if (upcomingEvents.length === 0) return null;
    
    return upcomingEvents.sort((a, b) => 
      new Date(a.start).getTime() - new Date(b.start).getTime()
    )[0];
  };
  
  const nextEvent = getNextUpcomingEvent();
  
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">My Calendars</CardTitle>
          <CardDescription>Toggle which calendars to display</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2.5">
            {sources.map((source) => (
              <div key={source.id} className="flex items-center space-x-2">
                <Checkbox 
                  id={`calendar-${source.id}`} 
                  checked={source.isVisible} 
                  onCheckedChange={() => onSourceToggle(source.id)}
                  className={source.isConnected ? source.color : 'text-muted-foreground'}
                />
                <label
                  htmlFor={`calendar-${source.id}`}
                  className={`text-sm font-medium flex items-center gap-1.5 ${!source.isConnected ? 'text-muted-foreground' : ''}`}
                >
                  <span>{source.name}</span>
                  {!source.isConnected && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-5 px-1.5 text-xs" 
                      onClick={() => {
                        setConnectingSource(source.type as 'google' | 'apple');
                        setShowConnectDialog(true);
                      }}
                    >
                      Connect
                    </Button>
                  )}
                </label>
              </div>
            ))}
          </div>
        </CardContent>
        <CardFooter className="border-t pt-4 flex justify-center">
          <Button variant="outline" className="w-full gap-2 text-sm" onClick={() => setShowConnectDialog(true)}>
            <Plus className="h-3.5 w-3.5" />
            Connect Calendar
          </Button>
        </CardFooter>
      </Card>
      
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-primary" />
            Up Next
          </CardTitle>
          <CardDescription>Your upcoming events</CardDescription>
        </CardHeader>
        <CardContent>
          {nextEvent ? (
            <div className="space-y-1">
              <h3 className="font-medium">{nextEvent.title}</h3>
              <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                <CalendarRange className="h-3.5 w-3.5" />
                <span>{format(new Date(nextEvent.start), 'EEEE, MMMM d')}</span>
              </div>
              <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                <span>
                  {format(new Date(nextEvent.start), 'h:mm a')} - {format(new Date(nextEvent.end), 'h:mm a')}
                </span>
              </div>
              {nextEvent.attendees && nextEvent.attendees.length > 0 && (
                <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-1">
                  <User className="h-3.5 w-3.5" />
                  <span>{nextEvent.attendees.length} attendees</span>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4 text-muted-foreground text-sm">
              No upcoming events
            </div>
          )}
        </CardContent>
      </Card>
      
      <Card className="bg-primary/5">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Calendar Integrations</CardTitle>
          <CardDescription>Connect to other services</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button variant="outline" className="w-full justify-start text-sm gap-2" onClick={() => {
            setConnectingSource('google');
            setShowConnectDialog(true);
          }}>
            <img src="https://upload.wikimedia.org/wikipedia/commons/a/a9/Google_Calendar_icon.svg" alt="Google Calendar" className="h-4 w-4" />
            <span>Google Calendar</span>
            <ExternalLink className="h-3 w-3 ml-auto" />
          </Button>
          
          <Button variant="outline" className="w-full justify-start text-sm gap-2" onClick={() => {
            setConnectingSource('apple');
            setShowConnectDialog(true);
          }}>
            <img src="https://upload.wikimedia.org/wikipedia/commons/5/5e/ICalendar_icon.png" alt="Apple Calendar" className="h-4 w-4" />
            <span>Apple Calendar</span>
            <ExternalLink className="h-3 w-3 ml-auto" />
          </Button>
        </CardContent>
      </Card>
      
      <Dialog open={showConnectDialog} onOpenChange={setShowConnectDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Connect {connectingSource === 'google' ? 'Google' : 'Apple'} Calendar</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              {connectingSource === 'google'
                ? "Connect your Google Calendar to sync events, get notifications, and manage your schedule."
                : "Connect your Apple Calendar to sync events across all your Apple devices."}
            </p>
            
            {connectingSource === 'google' && (
              <ol className="text-sm space-y-2 mb-4 list-decimal pl-4">
                <li>Sign in with your Google account</li>
                <li>Allow permissions to access your calendar</li>
                <li>Your events will automatically sync</li>
              </ol>
            )}
            
            {connectingSource === 'apple' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="apple-username">Apple ID</Label>
                  <Input id="apple-username" placeholder="your.email@icloud.com" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="apple-password">App-Specific Password</Label>
                  <Input id="apple-password" type="password" placeholder="••••••••••••" />
                  <p className="text-xs text-muted-foreground mt-1">
                    Create an app-specific password in your Apple ID settings
                  </p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConnectDialog(false)}>Cancel</Button>
            <Button onClick={() => {
              // In a real implementation, this would trigger the OAuth flow
              setShowConnectDialog(false);
            }}>
              Connect {connectingSource === 'google' ? 'Google' : 'Apple'} Calendar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarSidebar;
